import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLElement} [stickyButtonPrice] - Price shown inside the CTA button
 * @property {HTMLImageElement} [productImage] - Product image element
 */

/**
 * Sticky add-to-cart bar.
 * - Default: shows when buy buttons scroll out of view.
 * - always_fixed: stays fixed and hides while add-to-cart targets are visible.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {IntersectionObserver | null} */
  #addToCartVisibilityObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #addToCartVisible = false;

  /** @type {Set<Element>} */
  #visibleAddToCartTargets = new Set();

  get #alwaysFixed() {
    return this.dataset.alwaysFixed === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupResizeObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    if (this.#alwaysFixed) {
      this.#syncAlwaysFixedVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#addToCartVisibilityObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    this.#clearStickyClearance();
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (this.#alwaysFixed) {
      this.#setupAlwaysFixedObservers(buyButtonsBlock);
      this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
      return;
    }

    if (!productForm || !buyButtonsBlock || !footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Always-fixed mode: hide sticky while any ATC control is in view.
   * @param {Element | null | undefined} buyButtonsBlock
   */
  #setupAlwaysFixedObservers(buyButtonsBlock) {
    const targets = this.#getAddToCartTargets(buyButtonsBlock);
    if (targets.length === 0) {
      this.#showStickyBar();
      return;
    }

    this.#addToCartVisibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#visibleAddToCartTargets.add(entry.target);
          } else {
            this.#visibleAddToCartTargets.delete(entry.target);
          }
        });
        this.#addToCartVisible = this.#visibleAddToCartTargets.size > 0;
        this.#syncAlwaysFixedVisibility();
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      }
    );

    targets.forEach((target) => this.#addToCartVisibilityObserver?.observe(target));
  }

  /**
   * @param {Element | null | undefined} buyButtonsBlock
   * @returns {Element[]}
   */
  #getAddToCartTargets(buyButtonsBlock) {
    /** @type {Element[]} */
    const targets = [];
    const sectionElement = this.closest('.shopify-section');

    if (buyButtonsBlock) targets.push(buyButtonsBlock);

    sectionElement
      ?.querySelectorAll('[data-testid="standalone-add-to-cart"]')
      .forEach((el) => {
        if (!this.contains(el) && !buyButtonsBlock?.contains(el) && !targets.includes(el)) {
          targets.push(el);
        }
      });

    // Fallback: product-form ATC button outside sticky bar
    sectionElement
      ?.querySelectorAll('product-form-component [ref="addToCartButton"]')
      .forEach((el) => {
        if (!this.contains(el) && !buyButtonsBlock?.contains(el) && !targets.includes(el)) {
          targets.push(el);
        }
      });

    return targets;
  }

  #syncAlwaysFixedVisibility() {
    if (!this.#alwaysFixed) return;
    if (this.#addToCartVisible) {
      this.#hideStickyBar();
    } else {
      this.#showStickyBar();
    }
  }

  #setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#isStuck) this.#publishStickyClearance();
    });
    this.#resizeObserver.observe(this.refs.stickyBar);
  }

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const { addToCartButton, productImage } = this.refs;
    if (!addToCartButton) return;

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    /** @type {Element[]} */
    const animatedElements = [addToCartButton];

    // Fly-to-cart only when product image exists (full layout)
    if (cartIcon && productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
      animatedElements.push(flyToCartElement);
    }

    await onAnimationEnd(animatedElements);
    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    this.#updateButtonText();
    if (this.#isStuck) this.#publishStickyClearance();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.classList.add('is-visible');
    this.#publishStickyClearance();
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.classList.remove('is-visible');
    this.#clearStickyClearance();
  }

  #publishStickyClearance() {
    const { stickyBar } = this.refs;
    const rect = stickyBar.getBoundingClientRect();
    const gap = window.matchMedia('(max-width: 749px)').matches ? 12 : 16;
    const clearance = Math.ceil(rect.height + gap);
    document.documentElement.style.setProperty('--sticky-atc-clearance', `${clearance}px`);
    document.documentElement.classList.add('has-sticky-atc');
  }

  #clearStickyClearance() {
    document.documentElement.style.setProperty('--sticky-atc-clearance', '0px');
    document.documentElement.classList.remove('has-sticky-atc');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const showPriceOnButton = this.refs.stickyButtonPrice != null;

    const available = !addToCartButton.disabled;
    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1 && !showPriceOnButton) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
